import { computed, reactive, ref, watch } from "vue";
import parceriaService from "@/service/parceriaService";
import { STATUS_PARCERIA } from "@/constants/parcerias";

const TAMANHO_PAGINA_PADRAO = 8;

function obterNomeStatus(solicitacao) {
  if (!solicitacao) return "";
  const status =
    solicitacao.status ??
    solicitacao.statusParceria ??
    solicitacao.statusSolicitacao;
  if (typeof status === "string") return status;
  if (typeof status === "object" && status !== null) {
    return status.nome || status.status || status.descricao || status.valor || "";
  }
  return String(status || "");
}

/**
 * Composable exclusivo para o Dashboard de Parcerias da Coordenação (Quem Envia).
 * Gerencia as solicitações vinculadas a uma ação extensionista específica.
 */
export function useParceriasCoordenador({ acaoId = "", tamanhoPagina = TAMANHO_PAGINA_PADRAO } = {}) {
  const todasParcerias = ref([]);
  const filtros = reactive({ busca: "", status: "" });
  const paginaAtual = ref(0);
  const carregando = ref(false);
  const erro = ref("");
  const buscaAplicada = ref("");

  // ─── Ordenação: cronológica decrescente (mais recentes primeiro) ──────────
  const parceriasOrdenadas = computed(() => {
    return [...todasParcerias.value].sort((a, b) => {
      const dataA = a.dataSolicitacao ? new Date(a.dataSolicitacao).getTime() : 0;
      const dataB = b.dataSolicitacao ? new Date(b.dataSolicitacao).getTime() : 0;
      return dataB - dataA;
    });
  });

  // ─── Resumo computado para a Coordenação (Quem Envia) ─────────────────────
  const resumo = computed(() => {
    const lista = todasParcerias.value;
    return {
      total: lista.length,
      emEdicao: lista.filter((p) => p.status === STATUS_PARCERIA.EM_EDICAO).length,
      pendentes: lista.filter((p) => p.status === STATUS_PARCERIA.FORMULARIO_RECEBIDO).length,
      emAnalise: lista.filter((p) => p.status === STATUS_PARCERIA.EM_ANALISE).length,
      emArticulacao: lista.filter((p) => p.status === STATUS_PARCERIA.EM_ARTICULACAO_E_FORMALIZACAO).length,
      aguardandoReitoria: lista.filter((p) => p.status === STATUS_PARCERIA.AGUARDANDO_APROVACAO_REITORIA).length,
      formalizadas: lista.filter((p) => p.status === STATUS_PARCERIA.PARCERIA_FORMALIZADA).length,
      rejeitadas: lista.filter((p) => p.status === STATUS_PARCERIA.REJEITADA).length,
      canceladas: lista.filter((p) => p.status === STATUS_PARCERIA.CANCELADA).length,
    };
  });

  // ─── Filtragem por status e busca textual ────────────────────────────────
  const parceriasFiltradas = computed(() => {
    let lista = parceriasOrdenadas.value;

    if (filtros.status) {
      lista = lista.filter((p) => p.status === filtros.status);
    }

    const termo = buscaAplicada.value.trim().toLowerCase();
    if (termo) {
      lista = lista.filter((p) => {
        const texto = [
          p.tituloAcao,
          p.nomeAcao,
          p.nomeCoordenador,
          p.resumoProposta,
          p.tipoParceria,
          p.solicitante?.nome,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return texto.includes(termo);
      });
    }

    return lista;
  });

  // ─── Paginação computada ─────────────────────────────────────────────────
  const totalElementos = computed(() => parceriasFiltradas.value.length);
  const totalPaginas = computed(() =>
    Math.max(1, Math.ceil(totalElementos.value / tamanhoPagina))
  );

  const inicioPagina = computed(() =>
    totalElementos.value ? paginaAtual.value * tamanhoPagina + 1 : 0
  );

  const fimPagina = computed(() =>
    Math.min((paginaAtual.value + 1) * tamanhoPagina, totalElementos.value)
  );

  const parcerias = computed(() => {
    const inicio = paginaAtual.value * tamanhoPagina;
    return parceriasFiltradas.value.slice(inicio, inicio + tamanhoPagina);
  });

  watch(totalPaginas, (total) => {
    if (paginaAtual.value >= total) {
      paginaAtual.value = Math.max(total - 1, 0);
    }
  });

  // ─── Carregamento de dados da ação ────────────────────────────────────────
  async function carregarParcerias(idAcaoAtual = acaoId) {
    const idAcao = String(idAcaoAtual || "").trim();
    if (!idAcao) {
      erro.value = "Não foi possível identificar a ação atual.";
      todasParcerias.value = [];
      return;
    }

    carregando.value = true;
    erro.value = "";

    try {
      const response = await parceriaService.listarParcerias({
        acaoExtensaoId: idAcao,
        page: 0,
        size: 2000,
      });

      const dados = response?.data ?? response;
      const lista =
        dados?.content ??
        dados?.solicitacoes ??
        dados?.items ??
        (Array.isArray(dados) ? dados : []);

      // Filtra estritamente as parcerias da ação atual garantindo compatibilidade de tipos
      todasParcerias.value = lista
        .filter((parceria) => {
          const idParceria = String(
            parceria.acaoExtensaoId ?? parceria.acaoId ?? parceria.acao?.id ?? ""
          ).trim();
          return idParceria === idAcao;
        })
        .map((parceria) => {
          const s = String(obterNomeStatus(parceria)).trim().toUpperCase();
          const statusNormalizado = STATUS_PARCERIA[s] || s;
          return {
            ...parceria,
            nomeAcao: parceria.nomeAcao ?? parceria.tituloAcao ?? "Não informado",
            tituloAcao: parceria.tituloAcao ?? parceria.titulo ?? "Não informado",
            tipoParceria: parceria.tipoParceria ?? null,
            nomeCoordenador: parceria.nomeCoordenador ?? parceria.solicitante?.nome ?? null,
            resumoProposta: parceria.resumoProposta ?? parceria.resumo ?? "Não informado",
            dataSolicitacao: parceria.dataSolicitacao ?? null,
            status: statusNormalizado,
          };
        });

      paginaAtual.value = 0;
    } catch (err) {
      console.error("Erro ao carregar solicitações de parceria da coordenação:", err);
      erro.value = "Não foi possível carregar as solicitações de parceria.";
    } finally {
      carregando.value = false;
    }
  }

  // ─── Ações de filtro e navegação ─────────────────────────────────────────
  function aplicarFiltros() {
    buscaAplicada.value = filtros.busca;
    paginaAtual.value = 0;
  }

  function selecionarStatus(status) {
    filtros.status = status;
    aplicarFiltros();
  }

  function mudarPagina(pagina) {
    if (pagina < 0 || pagina >= totalPaginas.value) return;
    paginaAtual.value = pagina;
  }

  return {
    todasParcerias,
    parcerias,
    parceriasFiltradas,
    resumo,
    filtros,
    paginaAtual,
    totalPaginas,
    totalElementos,
    inicioPagina,
    fimPagina,
    carregando,
    erro,
    carregarParcerias,
    aplicarFiltros,
    selecionarStatus,
    mudarPagina,
    STATUS_PARCERIA,
  };
}
